import React, { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const HeroAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        title: "", title_ar: "",
        subtitle: "", subtitle_ar: "",
        image_url: "",
        button_text: "Browse Collection", button_text_ar: "تصفح المجموعة",
        button_link: "/shop",
        order: 0,
        is_active: 1
    });
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState("");
    const navigate = useNavigate();

    const isArabic = i18n.language?.startsWith('ar');



    const getHeaders = useCallback(() => {
        const token = getToken();
        if (token) return { Authorization: `Bearer ${token}` };
        return {};
    }, []);

    const fetchSlides = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/hero/admin`, { headers: getHeaders() });
            setSlides(res.data || []);
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    }, [getHeaders]);

    useEffect(() => { fetchSlides(); }, [fetchSlides]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const data = new FormData();
        Object.keys(form).forEach(key => data.append(key, form[key]));
        if (imageFile) data.append("image", imageFile);

        try {
            if (editing) {
                await axios.put(`${CONFIG.API_URL}/api/hero/${editing}`, data, { headers: getHeaders() });
            } else {
                await axios.post(`${CONFIG.API_URL}/api/hero`, data, { headers: getHeaders() });
            }
            setEditing(null);
            resetForm();
            fetchSlides();
        } catch (err) {
            alert(err.response?.data?.message || t("admin.hero_admin.delete_failed"));
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setForm({
            title: "", title_ar: "",
            subtitle: "", subtitle_ar: "",
            image_url: "",
            button_text: "Browse Collection", button_text_ar: "تصفح المجموعة",
            button_link: "/shop",
            order: 0,
            is_active: 1
        });
        setImageFile(null);
        setPreview("");
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("admin.hero_admin.delete_confirm"))) return;
        try {
            await axios.delete(`${CONFIG.API_URL}/api/hero/${id}`, { headers: getHeaders() });
            fetchSlides();
        } catch (err) {
            alert(t("admin.hero_admin.delete_failed"));
        }
    };

    const startEdit = (s) => {
        setEditing(s.id);
        setForm({
            title: s.title, title_ar: s.title_ar,
            subtitle: s.subtitle, subtitle_ar: s.subtitle_ar,
            image_url: s.image_url,
            button_text: s.button_text, button_text_ar: s.button_text_ar,
            button_link: s.button_link,
            order: s.order,
            is_active: s.is_active
        });
        setPreview(s.image_url);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };


    const [search, setSearch] = useState("");

    const filteredSlides = slides.filter(s =>
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        (s.subtitle && s.subtitle.toLowerCase().includes(search.toLowerCase())) ||
        s.id.toString().includes(search)
    );

    return (
        <div className="max-w-6xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex justify-between items-center mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t("admin.hero_admin.title")}</h2>
                    <p className="text-gray-500 mt-2 font-medium">{t("admin.hero_admin.subtitle")}</p>
                </div>
                <div className="flex items-center gap-4">

                    <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="mb-16 bg-brand-primary/5 p-10 rounded-[40px] border border-brand-primary/10">
                <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                    <span className="w-8 h-8 bg-brand-primary text-white rounded-xl flex items-center justify-center text-sm">
                        <i className={`fas ${editing ? "fa-edit" : "fa-plus"}`}></i>
                    </span>
                    {editing ? t("admin.hero_admin.update_slide") : t("admin.hero_admin.create_slide")}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.title_en")}</label>
                            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={t("admin.hero_admin.placeholders.title", "Exclusive Furniture")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" required />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.subtitle_en")}</label>
                            <textarea value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} placeholder={t("admin.hero_admin.placeholders.subtitle", "High-quality craftsmanship...")} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.btn_text_en")}</label>
                            <input value={form.button_text} onChange={e => setForm({ ...form, button_text: e.target.value })} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" />
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.title_ar")}</label>
                            <input value={form.title_ar} onChange={e => setForm({ ...form, title_ar: e.target.value })} dir="rtl" className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" required />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.subtitle_ar")}</label>
                            <textarea value={form.subtitle_ar} onChange={e => setForm({ ...form, subtitle_ar: e.target.value })} dir="rtl" className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.btn_text_ar")}</label>
                            <input value={form.button_text_ar} onChange={e => setForm({ ...form, button_text_ar: e.target.value })} dir="rtl" className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.bg_image")}</label>

                        <div
                            className="border-2 border-dashed border-brand-primary/20 rounded-[30px] bg-white p-8 text-center cursor-pointer hover:border-brand-primary transition-all group shadow-sm"
                            onClick={() => document.getElementById('hero-image-input').click()}
                        >
                            <input
                                id="hero-image-input"
                                type="file"
                                onChange={handleFileChange}
                                accept="image/*"
                                className="hidden"
                            />
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 bg-brand-primary/5 rounded-2xl flex items-center justify-center text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-all mb-2">
                                    <i className="fas fa-image text-xl"></i>
                                </div>
                                <p className="font-black text-gray-900 uppercase tracking-widest text-[10px] mb-1">
                                    {imageFile ? imageFile.name : t('admin.products_page.form.click_to_browse', 'Click to browse')}
                                </p>
                                {!imageFile && <p className="text-[10px] text-gray-400 font-medium">{t("admin.hero_admin.placeholders.no_file_selected", "No file selected.")}</p>}
                            </div>
                        </div>

                        {preview && (
                            <div className="mt-4 relative h-40 rounded-[30px] overflow-hidden border-2 border-brand-primary/10 shadow-lg">
                                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => { setImageFile(null); setPreview(""); setForm({ ...form, image_url: "" }); }} className="absolute top-3 right-3 bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-xs shadow-xl hover:scale-110 active:scale-90 transition-all">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        )}

                        {!preview && (
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <i className="fas fa-link text-gray-300 text-sm"></i>
                                </div>
                                <input
                                    value={form.image_url}
                                    onChange={e => setForm({ ...form, image_url: e.target.value })}
                                    placeholder={t("admin.hero_admin.placeholders.image_url", "Or paste URL...")}
                                    className="bg-white border-none pl-10 pr-4 py-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium text-sm transition-all"
                                />
                            </div>
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.display_order")}</label>
                        <input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) })} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold" />
                        <div className="space-y-2 mt-4">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.hero_admin.btn_link")}</label>
                            <input value={form.button_link} onChange={e => setForm({ ...form, button_link: e.target.value })} className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium" />
                        </div>
                    </div>
                    <div className="flex flex-col gap-4 pt-8 ml-4">
                        <div className="flex items-center gap-4">
                            <input type="checkbox" id="isActive" checked={form.is_active === 1} onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} className="w-5 h-5 accent-brand-primary" />
                            <label htmlFor="isActive" className="text-sm font-black text-gray-700 uppercase tracking-widest">{t("admin.hero_admin.active_slide")}</label>
                        </div>
                    </div>
                </div>

                <div className="mt-12 flex gap-4">
                    <button type="submit" disabled={saving} className="bg-brand-primary text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2">
                        {saving && <i className="fas fa-spinner fa-spin"></i>}
                        {saving ? t("admin.common.saving", "Saving...") : (editing ? t("admin.hero_admin.save_changes") : t("admin.hero_admin.create_btn"))}
                    </button>
                    {editing && <button type="button" onClick={() => { setEditing(null); resetForm(); }} className="text-gray-500 font-bold px-4">{t("admin.hero_admin.cancel")}</button>}
                </div>
            </form >

            <div className="mb-8 flex items-center bg-gray-50 p-4 rounded-[30px] border border-gray-100">
                <div className="relative w-full">
                    <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('admin.common.search_placeholder', 'Search by title or ID...')}
                        className="w-full pl-12 pr-4 py-3 rounded-2xl border-none bg-white shadow-sm focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium"
                    />
                </div>
            </div>

            {loading ? <div className="text-center py-20">< i className="fas fa-spinner fa-spin text-4xl text-brand-primary" ></i ></div > : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {filteredSlides.map(s => (
                        <div key={s.id} className="bg-white border border-gray-100 rounded-[40px] overflow-hidden shadow-sm hover:shadow-xl transition-all group p-6">
                            <div className="relative h-48 rounded-[30px] overflow-hidden mb-6">
                                <img src={s.image_url} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md bg-black/50 text-white">
                                        #{s.id}
                                    </span>
                                </div>
                                <div className="absolute top-4 right-4 flex gap-2">
                                    <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest backdrop-blur-md ${s.is_active ? "bg-green-500/80 text-white" : "bg-gray-500/80 text-white"}`}>
                                        {s.is_active ? t("admin.hero_admin.active") : t("admin.hero_admin.hidden")}
                                    </div>
                                    <div className="px-4 py-1.5 rounded-full bg-black/50 text-white text-[10px] font-black uppercase tracking-widest backdrop-blur-md">
                                        {t("admin.hero_admin.order", { order: s.order })}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="font-black text-xl text-gray-900">{s.title}</h4>
                                    <p className="text-gray-400 text-xs mt-1 italic line-clamp-1">{s.subtitle}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => startEdit(s)} className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all">
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button onClick={() => handleDelete(s.id)} className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center hover:bg-red-600 hover:text-white transition-all">
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div >
    );
});

export default HeroAdmin;
